import { api } from '../client';
import type { PageParams } from '@/types/api';
import type { Address } from '@/types/domain';
import type {
  AccountLink,
  Guardian,
  GuardianStatus,
  RelationshipType,
  StudentGuardian,
  UUID,
} from '@/types/domain';

export interface GuardianPayload {
  firstName: string;
  middleName?: string;
  lastName: string;
  email?: string;
  phone: string;
  alternatePhone?: string;
  occupation?: string;
  employer?: string;
  nationality?: string;
  nationalId?: string;
  address?: Address;
}

/** What a guardian is to a child, and what they are permitted to do. */
export interface LinkGuardianPayload {
  guardianId: UUID;
  relationshipType: RelationshipType;
  primaryGuardian: boolean;
  emergencyContact: boolean;
  pickupAuthorization: boolean;
  financialResponsibility: boolean;
  livesWithStudent: boolean;
  notes?: string;
}

export const guardiansApi = {
  list: (params: PageParams & { status?: GuardianStatus }) =>
    api.getPage<Guardian>('/guardians', { params }),
  get: (id: UUID) => api.get<Guardian>(`/guardians/${id}`),
  create: (payload: GuardianPayload) => api.post<Guardian>('/guardians', payload),
  update: (id: UUID, payload: GuardianPayload) => api.put<Guardian>(`/guardians/${id}`, payload),
  changeStatus: (id: UUID, status: GuardianStatus) =>
    api.patch<Guardian>(`/guardians/${id}/status`, { status }),
  /** Every child this guardian is responsible for — the point of a shared record. */
  students: (id: UUID) => api.get<StudentGuardian[]>(`/guardians/${id}/students`),

  /** The portal account behind this guardian; `userId` is absent when none is linked. */
  account: (id: UUID) => api.get<AccountLink>(`/guardians/${id}/account`),
  linkAccount: (id: UUID, userId: UUID) =>
    api.put<AccountLink>(`/guardians/${id}/account`, { userId }),
  unlinkAccount: (id: UUID) => api.delete<AccountLink>(`/guardians/${id}/account`),
};

export const studentGuardiansApi = {
  list: (studentId: UUID) => api.get<StudentGuardian[]>(`/students/${studentId}/guardians`),
  link: (studentId: UUID, payload: LinkGuardianPayload) =>
    api.post<StudentGuardian>(`/students/${studentId}/guardians`, payload),
  update: (studentId: UUID, guardianId: UUID, payload: LinkGuardianPayload) =>
    api.put<StudentGuardian>(`/students/${studentId}/guardians/${guardianId}`, payload),
  /**
   * Removes the link only. The guardian record is kept — it is very likely a
   * sibling's primary contact, and deleting the person because one child left
   * would strip that child's details without warning.
   */
  unlink: (studentId: UUID, guardianId: UUID) =>
    api.delete(`/students/${studentId}/guardians/${guardianId}`),
};
